import { rem } from 'polished';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useCurrentUser } from 'utils/hooks/useCurrentUser';

const Button = styled.button`
  align-items: center;
  background-color: transparent;
  border: none;
  color: currentColor;
  cursor: pointer;
  display: inline-flex;
  font-family: ${({ theme }) => theme.fonts.roboto};
  font-size: ${rem(12)};
  gap: 4px;
  line-height: 1;
  padding: 0;
  transform: translateY(2px);

  &:hover {
    color: ${({ theme }) => theme.colors.dimGray};
  }

  & svg {
    height: 14px;
  }
`;

export default function OneTrustButton() {
  const { user: currentUser } = useCurrentUser();
  const { t } = useTranslation();

  // Start with text that comes out of our own i18n translations.
  // Ultimately, we will use the text provided by OneTrust since it
  // needs to change for different locations and OneTrust will
  // translate that text for us.
  const [cookieText, setCookieText] = useState(t('cookieSettings'));

  const { langPref } = currentUser || {};
  const initializeOneTrust = useCallback(() => {
    // Make sure that OneTrust has initialized - it usually has but we
    // can't rely on it being there.
    if (typeof window.OneTrust?.GetDomainData === 'function') {
      // See if the user has set its language on our end and, if so,
      // pass that info along to OneTrust.
      if (langPref && typeof window.OneTrust?.changeLanguage === 'function') {
        window.OneTrust?.changeLanguage(langPref);
      }
      const data = window?.OneTrust?.GetDomainData();
      setCookieText(data?.CookieSettingButtonText || t('cookieSettings'));
    } else {
      window.setTimeout(initializeOneTrust, 500);
    }
  }, [langPref, t]);

  useEffect(initializeOneTrust, [initializeOneTrust]);

  function handleClick() {
    window.OneTrust?.ToggleInfoDisplay();
  }

  return (
    <Button id="ot-sdk-btn" onClick={handleClick} type="button">
      <svg xmlns="http://www.w3.org/2000/svg" xmlSpace="preserve" viewBox="0 0 30 14">
        <path
          fill="#fff"
          fillRule="evenodd"
          d="M7.4 12.8h6.8l3.1-11.6H7.4C4.2 1.2 1.6 3.8 1.6 7s2.6 5.8 5.8 5.8z"
          clipRule="evenodd"
        />
        <path
          fill="#06f"
          fillRule="evenodd"
          d="M22.6 0H7.4c-3.9 0-7 3.1-7 7s3.1 7 7 7h15.2c3.9 0 7-3.1 7-7s-3.2-7-7-7zm-21 7c0-3.2 2.6-5.8 5.8-5.8h9.9l-3.1 11.6H7.4c-3.2 0-5.8-2.6-5.8-5.8z"
          clipRule="evenodd"
        />
        <path
          fill="#fff"
          d="M24.6 4c.2.2.2.6 0 .8L22.5 7l2.2 2.2c.2.2.2.6 0 .8-.2.2-.6.2-.8 0l-2.2-2.2-2.2 2.2c-.2.2-.6.2-.8 0-.2-.2-.2-.6 0-.8L20.8 7l-2.2-2.2c-.2-.2-.2-.6 0-.8.2-.2.6-.2.8 0l2.2 2.2L23.8 4c.2-.2.6-.2.8 0z"
        />
        <path
          fill="#06f"
          d="M12.7 4.1c.2.2.3.6.1.8L8.6 9.8c-.1.1-.2.2-.3.2-.2.1-.5.1-.7-.1L5.4 7.7c-.2-.2-.2-.6 0-.8.2-.2.6-.2.8 0L8 8.6l3.8-4.5c.2-.2.6-.2.9 0z"
        />
      </svg>
      {cookieText}
    </Button>
  );
}
